import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from 'react';
import { Box, Switch, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import { useAssistant } from '@core/assistant/providers/Provider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import AssistantScriptDialogWrapper from '@core/assistant/Screens/DefaultScreen/AssistantScriptDialogWrapper';
import {
  formatSpotRegionName,
  regionNameToCamelCase,
} from '@core/assistant/providers/Regions';

import AssistantScriptsService from '@shared/services/AssistantScriptsService';
import AssistantRagStorageService from '@shared/services/AssistantRagStorageService';
import { formatSpotRegion } from '@shared/utils/assistantScript';
import {
  assistantMapKey,
  type IAssistantMapRef,
} from '@shared/utils/assistantMap';
import type {
  ICreateAssistantScript,
  IUpdateAssistantScript,
} from '@shared/interfaces/assistantScripts';
import type {
  ICreateAssistantRagVectorStorage,
  IUpdateAssistantRagVectorStorage,
} from '@shared/interfaces/assistantRagStorage';

import {
  aggregateEntities,
  countByFilter,
  entityMatchesSelection,
  filterEntities,
  tagMatchesTrigger,
} from './aggregate';
import CreateEntityPopover, { CreateOption } from './CreateEntityPopover';
import GeneralTab from './GeneralTab';
import { useEditorAiData } from './EditorAiDataContext';
import AssistantContextDialogWrapper from '@core/assistant/Screens/DefaultScreen/AssistantContextDialogWrapper';
import {
  AiTreeSection,
  KIND_TOKENS,
  MODE_ICON,
  TREE_SECTION_META,
  TREE_SECTION_ORDER,
  modeForEntity,
  sectionForEntity,
} from './kind';
import StatusRing from './StatusRing';
import { REQUIRE_SPOT_TRIGGER_LINK, useRegionIndex } from './useRegionIndex';
import type { AiEntity, AiEntityFilter, OverallState } from './types';
import { useAiScriptsTreeStyles } from './styles';
import { EditorNodeKind } from '../editorCategories';
import { useEditorMode } from '../EditorModeContext';
import { useDrift } from '../DriftContext';
import { sortByName } from '../sortUtils';

interface IFilterDef {
  id: AiEntityFilter;
  labelKey: string;
}

const FILTERS: IFilterDef[] = [
  { id: 'all', labelKey: 'editorMode.aiFilter.all' },
  { id: 'full', labelKey: 'editorMode.aiFilter.ready' },
  { id: 'partial', labelKey: 'editorMode.aiFilter.partial' },
  { id: 'issues', labelKey: 'editorMode.aiFilter.issues' },
];

// Whether the active section has any row to sort — feeds the shared sort-toggle
// button's disabled state (SceneTree.tsx panel header). 'general' has no entity
// rows at all.
const getIsAiTreeSectionEmpty = (
  section: AiTreeSection,
  scriptsRowCount: number,
  knowledgeRowCount: number,
): boolean => {
  if (section === 'scripts') {
    return scriptsRowCount === 0;
  }
  if (section === 'knowledge') {
    return knowledgeRowCount === 0;
  }
  return true;
};

const AiScriptsTreeSection = () => {
  const { t: translate } = useTranslation();
  const classes = useAiScriptsTreeStyles();
  const {
    selectedNode,
    selectRegionScript,
    aiTreeSection,
    setAiTreeSection,
    followLiveTrigger,
    setFollowLiveTrigger,
    sortDirections,
    setIsAiScriptsListEmpty,
  } = useEditorMode();
  const {
    currentBrandingId,
    currentRoom,
    aiTriggerName,
    brandingSpots,
    roomSpots,
    isBrandingModeEnabled,
  } = useMytaverseEvent();
  const createButtonRef = useRef<HTMLButtonElement | null>(null);
  // Ref to the currently selected/live row so we can scroll it into view —
  // mirrors the branding spot-tree behaviour (SceneTree.renderSpots).
  const focusedRowRef = useRef<HTMLButtonElement | null>(null);

  const assistant = useAssistant();
  // Scripts + RAG come from the editor-scoped context so the tree and the
  // inspector share a single instance — a mutation in either panel refetches
  // both views together.
  const { scripts, ragItems, refetchAll: refetchAiData } = useEditorAiData();
  const { triggerDrift } = useDrift();
  const clearPrerenderedAudio = assistant?.clearPrerenderedAudio;
  // Everything is scoped to the editor's selected branding — listing AND
  // creation. New scripts/knowledge must be tagged with this branding (not the
  // assistant's live/runtime branding) so they appear in the current view.
  const brandingId = currentBrandingId ?? undefined;
  // The record, not just its key: the scope filter also matches rows still
  // written with the map's old display name (MV-1891). `currentMapName` is
  // already canonical, so it only ever stands in as a UE identifier.
  const map: IAssistantMapRef | undefined =
    currentRoom?.map ??
    (assistant?.currentMapName
      ? { ueIdentifier: assistant.currentMapName }
      : undefined);
  const mapName = assistantMapKey(map);
  const audioLanguage = assistant?.audioLanguage;

  // AI trigger regions + the spots each one owns (region.spots[]). The bridge
  // between an AI trigger ("Expo #1") and its spots ("WallArt_Pic_01"), so one
  // script can be created for both at once.
  const regionIndex = useRegionIndex(mapName);
  const knownAiTriggerNames = regionIndex.regionNames;
  const joinTriggerName = regionIndex.startingPointRegionName ?? 'landing';

  const [filter, setFilter] = useState<AiEntityFilter>('all');
  // The Scripts section splits rows into two collapsible buckets: Configured
  // (have a script) and Empty (a trigger/spot with no script yet).
  const [sectionsCollapsed, setSectionsCollapsed] = useState<{
    configured: boolean;
    newInBuild: boolean;
    dynamicTrigger: boolean;
    spawnedTrigger: boolean;
    empty: boolean;
  }>({
    configured: false,
    newInBuild: false,
    dynamicTrigger: false,
    spawnedTrigger: false,
    empty: false,
  });

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [scriptDialogState, setScriptDialogState] = useState<{
    // Primary/canonical trigger shown in the dialog (region name when creating
    // for an AI trigger, otherwise the spot id).
    trigger: string;
    triggerType: 'area_entry' | 'ui_click';
    // Full trigger list persisted on save — [regionName, ...spots] for a
    // region, [spotId] for a standalone spot.
    triggers: string[];
  } | null>(null);
  const [knowledgeDialogState, setKnowledgeDialogState] = useState<{
    trigger: string;
  } | null>(null);

  // Scoped to the currently selected branding (top-left dropdown) AND the
  // current map, so the tree only shows scripts/knowledge that belong to the
  // map being edited. A mutation refetches the full list (refetchAll) and this
  // re-aggregates against the current brandingId + mapName.
  const entities = useMemo(() => {
    const all = aggregateEntities({
      scripts,
      ragItems,
      map: map,
      brandingId,
      knownAiTriggerNames,
      joinTriggerName,
    });
    if (!REQUIRE_SPOT_TRIGGER_LINK) {
      return all;
    }
    // A spot (ui_click) entity is only surfaced when it is linked to a
    // trigger — i.e. owned by a scriptable UE region. Regions themselves
    // (ai_trigger/landing) and 3D object POIs are unaffected.
    return all.filter(
      (entity) =>
        entity.category !== 'spot' ||
        !!regionIndex.spotToRegion[entity.primaryTrigger.toLowerCase()],
    );
  }, [
    scripts,
    ragItems,
    map,
    brandingId,
    knownAiTriggerNames,
    joinTriggerName,
    regionIndex,
  ]);

  // Entities for the active section: 'knowledge' → orphan RAG docs;
  // 'scripts' → AI triggers + spots + objects + landing (everything else).
  const sectionEntities = useMemo(
    () => entities.filter((e) => sectionForEntity(e) === aiTreeSection),
    [entities, aiTreeSection],
  );

  const counts = useMemo(
    () => countByFilter(sectionEntities),
    [sectionEntities],
  );
  const visible = useMemo(
    () =>
      sortByName(
        filterEntities(sectionEntities, filter),
        (entity) => entity.name,
        sortDirections.aiScripts,
      ),
    [sectionEntities, filter, sortDirections.aiScripts],
  );

  const selectedScriptId =
    selectedNode?.kind === EditorNodeKind.REGION_SCRIPT
      ? selectedNode.scriptId
      : null;
  const selectedTrigger =
    selectedNode?.kind === EditorNodeKind.REGION_SCRIPT
      ? selectedNode.trigger
      : null;

  const isSelected = useCallback(
    (entity: AiEntity): boolean =>
      entityMatchesSelection(entity, selectedScriptId, selectedTrigger),
    [selectedScriptId, selectedTrigger],
  );

  // Live highlight: when browsing the Scripts section with the inspector
  // closed, mark the row matching the trigger UE is currently reporting.
  const isInspectorClosed = !selectedNode;
  const isLive = useCallback(
    (entity: AiEntity): boolean => {
      if (aiTreeSection !== 'scripts' || !isInspectorClosed || !aiTriggerName) {
        return false;
      }
      if (tagMatchesTrigger(aiTriggerName, entity.primaryTrigger)) {
        return true;
      }
      return entity.aliases.some((alias) =>
        tagMatchesTrigger(aiTriggerName, alias),
      );
    },
    [aiTreeSection, isInspectorClosed, aiTriggerName],
  );

  const handleSelect = useCallback(
    (entity: AiEntity): void => {
      selectRegionScript(
        entity.primaryTrigger,
        entity.script?.scriptId ?? null,
      );
    },
    [selectRegionScript],
  );

  // Bring the selected / live-highlighted row into view whenever the focus
  // target changes.
  useEffect(() => {
    const node = focusedRowRef.current;
    if (node) {
      node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedScriptId, selectedTrigger, aiTriggerName, aiTreeSection]);

  const handleSwitchSection = useCallback(
    (section: AiTreeSection) => {
      setAiTreeSection(section);
      setFilter('all');
    },
    [setAiTreeSection],
  );

  // Resolve an id (region name, spot id, or free text) into a create payload.
  // Region-canonical: picking an AI trigger — or any spot that lives inside one
  // — produces a single script bound to [regionName, ...spots]. A standalone
  // spot becomes a ui_click script bound to just that spot.
  const buildCreateTarget = useCallback(
    (
      id: string,
    ): {
      trigger: string;
      triggerType: 'area_entry' | 'ui_click';
      triggers: string[];
    } => {
      const regionName = regionIndex.regionNames.find(
        (name) => name.toLowerCase() === id.toLowerCase(),
      );
      const owningRegion =
        regionName ?? regionIndex.spotToRegion[id.toLowerCase()];
      if (owningRegion) {
        const spots = regionIndex.regionSpots[owningRegion] ?? [];
        return {
          trigger: owningRegion,
          triggerType: 'area_entry',
          triggers: [owningRegion, ...spots],
        };
      }
      return { trigger: id, triggerType: 'ui_click', triggers: [id] };
    },
    [regionIndex],
  );

  const handleStartCreate = useCallback(
    (id: string) => {
      setPopoverOpen(false);
      if (aiTreeSection === 'knowledge') {
        setKnowledgeDialogState({ trigger: formatSpotRegionName(id) || id });
        return;
      }
      setScriptDialogState(buildCreateTarget(id));
    },
    [aiTreeSection, buildCreateTarget],
  );

  // The Create button jumps straight to the live trigger UE is reporting
  // (SYNC_DATA_UE.audioTrigger.aiTriggerId) when there is one — otherwise it
  // opens the picker.
  const handleCreateClick = useCallback(() => {
    if (aiTreeSection === 'scripts' && aiTriggerName) {
      handleStartCreate(aiTriggerName);
      return;
    }
    setPopoverOpen((prev) => !prev);
  }, [aiTreeSection, aiTriggerName, handleStartCreate]);

  const handleScriptSaved = useCallback(
    async (data: ICreateAssistantScript | IUpdateAssistantScript) => {
      const state = scriptDialogState;
      if (!state) {
        return;
      }
      const payload: ICreateAssistantScript = {
        ...(data as ICreateAssistantScript),
        // Persist the full trigger set (region + its spots) so the script
        // answers to both walk-in and spot-click — the dialog only knows the
        // single primary trigger.
        ...(state.triggers.length ? { scriptTrigger: state.triggers } : {}),
      };
      await AssistantScriptsService.createAssistantScript(payload);
      clearPrerenderedAudio?.();
      await refetchAiData();
      setScriptDialogState(null);
      // Focus the new script's entity in the inspector.
      selectRegionScript(state.trigger, null);
    },
    [
      scriptDialogState,
      clearPrerenderedAudio,
      refetchAiData,
      selectRegionScript,
    ],
  );

  const handleKnowledgeSubmit = useCallback(
    async (
      values:
        | ICreateAssistantRagVectorStorage
        | IUpdateAssistantRagVectorStorage,
    ) => {
      const state = knowledgeDialogState;
      if (!state) {
        return;
      }
      const payload: ICreateAssistantRagVectorStorage = {
        ...(values as ICreateAssistantRagVectorStorage),
        ...(brandingId ? { brandingId } : {}),
        ...(mapName ? { mapName } : {}),
      };
      await AssistantRagStorageService.createAssistantRagStorageItem(payload);
      await refetchAiData();
      setKnowledgeDialogState(null);
      // If a script already exists for this region, snap selection to it so
      // the new knowledge appears under the right entity right away.
      const camelTrigger = regionNameToCamelCase(state.trigger);
      const matchingScript = scripts.find((s) => {
        const triggers = Array.isArray(s.scriptTrigger)
          ? s.scriptTrigger
          : [s.scriptTrigger];
        return triggers.some((t) => regionNameToCamelCase(t) === camelTrigger);
      });
      selectRegionScript(state.trigger, matchingScript?.scriptId ?? null);
    },
    [
      knowledgeDialogState,
      brandingId,
      mapName,
      refetchAiData,
      scripts,
      selectRegionScript,
    ],
  );

  const meta = TREE_SECTION_META[aiTreeSection];

  // Spots come from the configuration spots list (the /brandings/:id/spots payload);
  // AI trigger regions come from the map regions (see useRegionIndex.ts).
  const spotOptions = useMemo<CreateOption[]>(() => {
    const source =
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;
    const list = Array.isArray(source) ? source : [];
    const seenUeIds = new Set<string>();
    return list.flatMap((spot): CreateOption[] => {
      const raw = spot as { ueIdentifier?: unknown; name?: unknown };
      const ueId =
        typeof raw.ueIdentifier === 'string' ? raw.ueIdentifier.trim() : '';
      if (!ueId || seenUeIds.has(ueId)) {
        return [];
      }
      seenUeIds.add(ueId);
      const name =
        typeof raw.name === 'string' && raw.name.trim() ? raw.name : ueId;
      const option: CreateOption = { id: ueId, label: name };
      if (name !== ueId) {
        option.sublabel = ueId;
      }
      return [option];
    });
  }, [currentBrandingId, isBrandingModeEnabled, brandingSpots, roomSpots]);

  const regionOptions = useMemo<CreateOption[]>(
    () =>
      knownAiTriggerNames.map((name) => {
        const label = formatSpotRegion(name) || name;
        const option: CreateOption = { id: name, label };
        if (label !== name) {
          option.sublabel = name;
        }
        return option;
      }),
    [knownAiTriggerNames],
  );

  // Spots not owned by any AI trigger region — surfaced on their own so they
  // aren't lost when modes merge. When REQUIRE_SPOT_TRIGGER_LINK is on, these
  // are exactly the spots NOT linked to a trigger, so they must never be
  // offered as script-creation targets (see createOptions/emptyOptions).
  const standaloneSpotOptions = useMemo<CreateOption[]>(
    () =>
      spotOptions.filter(
        (option) => !regionIndex.spotToRegion[option.id.toLowerCase()],
      ),
    [spotOptions, regionIndex],
  );

  // Spot options gated to only those linked to a trigger, for surfaces where
  // REQUIRE_SPOT_TRIGGER_LINK restricts creation (knowledge picker).
  const linkedSpotOptions = useMemo<CreateOption[]>(
    () =>
      REQUIRE_SPOT_TRIGGER_LINK
        ? spotOptions.filter(
            (option) => !!regionIndex.spotToRegion[option.id.toLowerCase()],
          )
        : spotOptions,
    [spotOptions, regionIndex],
  );

  // Standalone spot options gated for script creation — REQUIRE_SPOT_TRIGGER_LINK
  // means an unlinked (non-region-owned) spot can never get its own script.
  const scriptableStandaloneSpotOptions = useMemo<CreateOption[]>(
    () => (REQUIRE_SPOT_TRIGGER_LINK ? [] : standaloneSpotOptions),
    [standaloneSpotOptions],
  );

  // The synthetic 'landing' pseudo-trigger — offered only when the map has no
  // real isStartingPoint region (joinTriggerName then falls back to it), so
  // the join-trigger row always exists somewhere to create/view, mirroring
  // admin's synthetic 'landing' row.
  const landingOption = useMemo<CreateOption | null>(() => {
    if (regionIndex.startingPointRegionName) {
      return null;
    }
    return {
      id: 'landing',
      label: translate('editorMode.aiLandingOption', 'Landing'),
    };
  }, [regionIndex.startingPointRegionName, translate]);

  const createOptions = useMemo<CreateOption[]>(() => {
    const landing = landingOption ? [landingOption] : [];
    if (aiTreeSection === 'knowledge') {
      // Knowledge can attach to any trigger, or a spot linked to a trigger.
      return [...regionOptions, ...linkedSpotOptions, ...landing];
    }
    return [...regionOptions, ...scriptableStandaloneSpotOptions, ...landing];
  }, [
    aiTreeSection,
    regionOptions,
    linkedSpotOptions,
    scriptableStandaloneSpotOptions,
    landingOption,
  ]);

  const liveOption = useMemo<CreateOption | null>(() => {
    if (aiTreeSection === 'scripts' && aiTriggerName) {
      const label = formatSpotRegion(aiTriggerName) || aiTriggerName;
      return { id: aiTriggerName, label };
    }
    return null;
  }, [aiTreeSection, aiTriggerName]);

  // Whether any Scripts-section entity already covers a given identifier (its
  // own trigger or, for a region, any of the spots inside it).
  const isCoveredBy = useCallback(
    (target: string): boolean =>
      sectionEntities.some(
        (entity) =>
          tagMatchesTrigger(target, entity.primaryTrigger) ||
          entity.aliases.some((alias) => tagMatchesTrigger(target, alias)),
      ),
    [sectionEntities],
  );

  // Scripts section: every trigger/spot with no script yet. A region is empty
  // only when neither its name nor any of its spots has a script. Standalone
  // spots are empty when they have no script of their own.
  const emptyOptions = useMemo<CreateOption[]>(() => {
    if (aiTreeSection !== 'scripts') {
      return [];
    }
    const emptyRegions = regionOptions.filter((option) => {
      const spots = regionIndex.regionSpots[option.id] ?? [];
      const covered =
        isCoveredBy(option.id) || spots.some((spot) => isCoveredBy(spot));
      return !covered;
    });
    const emptySpots = scriptableStandaloneSpotOptions.filter(
      (option) => !isCoveredBy(option.id),
    );
    const emptyLanding =
      landingOption && !isCoveredBy(landingOption.id) ? [landingOption] : [];
    const seenIds = new Set<string>();
    return [...emptyRegions, ...emptySpots, ...emptyLanding].filter(
      (option) => {
        if (seenIds.has(option.id)) {
          return false;
        }
        seenIds.add(option.id);
        return true;
      },
    );
  }, [
    aiTreeSection,
    regionOptions,
    scriptableStandaloneSpotOptions,
    regionIndex,
    isCoveredBy,
    landingOption,
  ]);

  // Drift overlay (shared engine): split the Configured / Empty buckets so
  // triggers new in the active build and scripts whose region was removed
  // surface as their own groups, mirroring the spot tree.
  const newTriggerRows = useMemo(
    () =>
      sortByName(
        triggerDrift.rows.filter((row) => row.status === 'new'),
        (row) => formatSpotRegion(row.trigger) || row.trigger,
        sortDirections.aiScripts,
      ),
    [triggerDrift, sortDirections.aiScripts],
  );
  // Still computed, no longer rendered: a script whose region the build dropped is simply not listed
  // (it is reachable in the admin console). It used to get a red "Orphaned scripts" group, which
  // reported a build change to someone who only authors scripts — see BrandingBar for the same call.
  const orphanTriggerRows = useMemo(
    () =>
      sortByName(
        triggerDrift.rows.filter((row) => row.status === 'orphaned'),
        (row) => formatSpotRegion(row.trigger) || row.trigger,
        sortDirections.aiScripts,
      ),
    [triggerDrift, sortDirections.aiScripts],
  );
  const orphanedScriptIds = useMemo(
    () =>
      new Set(
        orphanTriggerRows
          .map((row) => row.scriptId)
          .filter((id): id is string => Boolean(id)),
      ),
    [orphanTriggerRows],
  );
  const newTriggerNames = useMemo(
    () => new Set(newTriggerRows.map((row) => row.trigger.toLowerCase())),
    [newTriggerRows],
  );
  // Unscripted triggers reported by dynamically-spawned level chunks — a
  // separate fact from "new in build" (computeTriggerDrift), so it gets its
  // own set/group rather than reusing the build-drift one.
  const dynamicTriggerNamesSet = useMemo(
    () => new Set(regionIndex.dynamicTriggerNames.map((n) => n.toLowerCase())),
    [regionIndex.dynamicTriggerNames],
  );
  // Unscripted trigger regions the web spawned and saved — their own group
  // too, because they are neither in the build nor discovered by walking in.
  // A spawned name the build also declares is one region seen from two sides
  // and stays in the build's groups; only names the build does not know are
  // "spawned" here.
  const spawnedTriggerNamesSet = useMemo(() => {
    const buildNames = new Set(
      regionIndex.buildRegionNames.map((n) => n.toLowerCase()),
    );
    return new Set(
      regionIndex.spawnedTriggerNames
        .map((n) => n.toLowerCase())
        .filter((n) => !buildNames.has(n)),
    );
  }, [regionIndex.buildRegionNames, regionIndex.spawnedTriggerNames]);
  const spawnedTriggerOptions = useMemo(
    () =>
      sortByName(
        emptyOptions.filter((option) =>
          spawnedTriggerNamesSet.has(option.id.toLowerCase()),
        ),
        (option) => option.label,
        sortDirections.aiScripts,
      ),
    [emptyOptions, spawnedTriggerNamesSet, sortDirections.aiScripts],
  );
  const dynamicTriggerOptions = useMemo(
    () =>
      sortByName(
        emptyOptions.filter((option) =>
          dynamicTriggerNamesSet.has(option.id.toLowerCase()),
        ),
        (option) => option.label,
        sortDirections.aiScripts,
      ),
    [emptyOptions, dynamicTriggerNamesSet, sortDirections.aiScripts],
  );
  // Orphaned scripts have a script, so they would otherwise show as Configured;
  // new-in-build, dynamically-discovered and spawned triggers are unscripted, so
  // they would show in Empty. Pull all four out into their dedicated groups below.
  const configuredEntities = useMemo(
    () =>
      sortByName(
        sectionEntities.filter(
          (entity) =>
            !(
              entity.script?.scriptId &&
              orphanedScriptIds.has(entity.script.scriptId)
            ),
        ),
        (entity) => entity.name,
        sortDirections.aiScripts,
      ),
    [sectionEntities, orphanedScriptIds, sortDirections.aiScripts],
  );
  const noScriptOptions = useMemo(
    () =>
      sortByName(
        emptyOptions.filter(
          (option) =>
            !newTriggerNames.has(option.id.toLowerCase()) &&
            !dynamicTriggerNamesSet.has(option.id.toLowerCase()) &&
            !spawnedTriggerNamesSet.has(option.id.toLowerCase()),
        ),
        (option) => option.label,
        sortDirections.aiScripts,
      ),
    [
      emptyOptions,
      newTriggerNames,
      dynamicTriggerNamesSet,
      spawnedTriggerNamesSet,
      sortDirections.aiScripts,
    ],
  );

  // Whether the active section (Scripts or Knowledge) has any row to sort right now.
  // Reported to context via a layout effect (not a plain effect) so it lands before
  // the browser paints — SceneTree's header button reads it to decide whether to
  // show at all, and a post-paint update there would flash the button in or out.
  const isCurrentSectionSortEmpty = getIsAiTreeSectionEmpty(
    aiTreeSection,
    configuredEntities.length +
      noScriptOptions.length +
      newTriggerRows.length +
      dynamicTriggerOptions.length +
      spawnedTriggerOptions.length,
    visible.length,
  );
  useLayoutEffect(() => {
    setIsAiScriptsListEmpty(isCurrentSectionSortEmpty);
  }, [isCurrentSectionSortEmpty, setIsAiScriptsListEmpty]);

  const createPlaceholder =
    aiTreeSection === 'scripts'
      ? translate('editorMode.aiCreateSearchScripts')
      : translate('editorMode.aiCreateSearchAny');

  const createLabel =
    aiTreeSection === 'knowledge'
      ? translate('editorMode.aiMode.addKnowledge')
      : translate('editorMode.aiCreateScript', 'Create script');

  const isRegionId = useCallback(
    (id: string): boolean =>
      regionIndex.regionNames.some(
        (name) => name.toLowerCase() === id.toLowerCase(),
      ),
    [regionIndex],
  );

  const renderRow = (params: {
    key: string;
    name: string;
    subtitle: string;
    state: OverallState;
    selected: boolean;
    live: boolean;
    icon: ComponentType<{ sx?: object }>;
    iconColor: string;
    onClick: () => void;
    badge?: string;
    isEntryPoint?: boolean;
    testId: string;
  }) => {
    const RowIcon = params.icon;
    return (
      <Tooltip
        key={params.key}
        title={params.name}
        placement="right"
        enterDelay={350}
        arrow
      >
        <Box
          component="button"
          type="button"
          ref={params.selected || params.live ? focusedRowRef : undefined}
          sx={[
            classes.treeRow,
            params.selected && classes.treeRowSelected,
            params.live && classes.treeRowLive,
          ]}
          onClick={params.onClick}
          aria-pressed={params.selected}
          data-testid={params.testId}
        >
          <RowIcon sx={{ ...classes.rowIcon, color: params.iconColor }} />
          <Box sx={classes.rowName}>
            <Typography sx={classes.rowTitle}>{params.name}</Typography>
            {params.subtitle ? (
              <Typography sx={classes.rowSubtitle}>
                {params.subtitle}
              </Typography>
            ) : null}
          </Box>
          {params.isEntryPoint ? (
            <Tooltip
              title={translate(
                'editorMode.aiEntryPointTooltip',
                'Map entry point where user starts their journey',
              )}
              placement="top"
              enterDelay={300}
            >
              <Box component="span" sx={classes.entryPointRowBadge}>
                {translate('editorMode.aiEntryPointChip', 'Map entry')}
                <InfoOutlinedIcon />
              </Box>
            </Tooltip>
          ) : null}
          {params.badge ? (
            <Box component="span" sx={classes.driftRowBadge}>
              {params.badge}
            </Box>
          ) : null}
          <StatusRing state={params.state} size={22} />
        </Box>
      </Tooltip>
    );
  };

  const renderEntityRow = (entity: AiEntity) => {
    const selected = isSelected(entity);
    const mode = modeForEntity(entity);
    return renderRow({
      key: entity.id,
      name: entity.name,
      subtitle:
        entity.primaryTrigger || entity.contextItems[0]?.tags?.join(', ') || '',
      state: entity.overallState,
      selected,
      live: !selected && isLive(entity),
      icon: MODE_ICON[mode],
      iconColor: KIND_TOKENS[mode].main,
      onClick: () => handleSelect(entity),
      isEntryPoint: entity.kind === 'landing',
      testId: `ai-tree-row-${entity.id}`,
    });
  };

  const renderEmptyOptionRow = (option: CreateOption) => {
    const selected = selectedTrigger === option.id && !selectedScriptId;
    const isEntryPoint = tagMatchesTrigger(joinTriggerName, option.id);
    const regionLike = isRegionId(option.id) || isEntryPoint;
    // An empty AI-trigger row still gets the live accent when UE reports it.
    const live =
      !selected &&
      aiTreeSection === 'scripts' &&
      isInspectorClosed &&
      !!aiTriggerName &&
      tagMatchesTrigger(aiTriggerName, option.id);
    return renderRow({
      key: `empty:${option.id}`,
      name: option.label,
      subtitle: option.sublabel || option.id,
      state: 'empty',
      selected,
      live,
      icon: regionLike ? MODE_ICON.trigger : MODE_ICON.spot,
      iconColor: '#62666f',
      onClick: () => selectRegionScript(option.id, null),
      isEntryPoint,
      testId: `ai-tree-row-empty-${option.id}`,
    });
  };

  const renderNewTriggerRow = (row: (typeof triggerDrift.rows)[number]) => {
    const selected = selectedTrigger === row.trigger && !selectedScriptId;
    return renderRow({
      key: `new:${row.trigger}`,
      name: formatSpotRegion(row.trigger) || row.trigger,
      subtitle: translate('editorMode.driftNewTriggerSub'),
      state: 'empty',
      selected,
      live: false,
      icon: MODE_ICON.trigger,
      iconColor: '#f0b357',
      onClick: () => selectRegionScript(row.trigger, null),
      badge: translate('editorMode.driftNew', 'New'),
      isEntryPoint: tagMatchesTrigger(joinTriggerName, row.trigger),
      testId: `ai-tree-row-new-${row.trigger}`,
    });
  };

  const renderDynamicTriggerRow = (option: CreateOption) => {
    const selected = selectedTrigger === option.id && !selectedScriptId;
    const isEntryPoint = tagMatchesTrigger(joinTriggerName, option.id);
    const live =
      !selected &&
      aiTreeSection === 'scripts' &&
      isInspectorClosed &&
      !!aiTriggerName &&
      tagMatchesTrigger(aiTriggerName, option.id);
    return renderRow({
      key: `dynamic:${option.id}`,
      name: option.label,
      subtitle: translate('editorMode.driftDynamicTriggerSub'),
      state: 'empty',
      selected,
      live,
      icon: MODE_ICON.trigger,
      iconColor: '#f0b357',
      onClick: () => selectRegionScript(option.id, null),
      badge: translate('editorMode.driftDynamicTriggerBadge', 'Live'),
      isEntryPoint,
      testId: `ai-tree-row-dynamic-${option.id}`,
    });
  };

  /**
   * Renders an unscripted trigger region the web spawned and saved, keyed by
   * its saved TriggerName — the same row the dynamic group draws, with the
   * spawned copy and badge.
   *
   * @param option - The empty (no-script) option for the region.
   */
  const renderSpawnedTriggerRow = (option: CreateOption) => {
    const selected = selectedTrigger === option.id && !selectedScriptId;
    const isEntryPoint = tagMatchesTrigger(joinTriggerName, option.id);
    const live =
      !selected &&
      aiTreeSection === 'scripts' &&
      isInspectorClosed &&
      !!aiTriggerName &&
      tagMatchesTrigger(aiTriggerName, option.id);
    return renderRow({
      key: `spawned:${option.id}`,
      name: option.label,
      subtitle: translate('editorMode.driftSpawnedTriggerSub'),
      state: 'empty',
      selected,
      live,
      icon: MODE_ICON.trigger,
      iconColor: '#f0b357',
      onClick: () => selectRegionScript(option.id, null),
      badge: translate('editorMode.driftSpawnedTriggerBadge', 'Spawned'),
      isEntryPoint,
      testId: `ai-tree-row-spawned-${option.id}`,
    });
  };

  const renderSectionHeader = (
    labelKey: string,
    count: number,
    collapsed: boolean,
    onToggle: () => void,
    testId: string,
    accentSx?: object,
  ) => (
    <Box
      component="button"
      type="button"
      sx={classes.collapsibleHeader}
      onClick={onToggle}
      aria-expanded={!collapsed}
      data-testid={testId}
    >
      <ExpandMoreOutlinedIcon
        sx={[
          classes.collapsibleChevron,
          collapsed && classes.collapsibleChevronCollapsed,
        ]}
      />
      <Box
        component="span"
        sx={[classes.collapsibleHeaderText, ...(accentSx ? [accentSx] : [])]}
      >
        {translate(labelKey)}
      </Box>
      <Box component="span" sx={classes.collapsibleCount}>
        {count}
      </Box>
    </Box>
  );

  return (
    <Box sx={classes.root}>
      <Box sx={classes.modeSwitcher} role="tablist">
        {TREE_SECTION_ORDER.map((section) => {
          const SectionIcon = TREE_SECTION_META[section].icon;
          const active = aiTreeSection === section;
          // Both options share the violet trigger accent for hover/select.
          const tokens = KIND_TOKENS.trigger;
          const activeSx = active
            ? {
                backgroundColor: tokens.bg,
                borderColor: tokens.border,
                color: tokens.text,
                '& svg': { color: tokens.main },
              }
            : null;
          return (
            <Box
              key={section}
              component="button"
              type="button"
              role="tab"
              aria-selected={active}
              sx={[classes.modeSeg, ...(activeSx ? [activeSx] : [])]}
              onClick={() => handleSwitchSection(section)}
              data-testid={`ai-tree-section-${section}`}
            >
              <SectionIcon />
              <Box component="span" sx={classes.modeSegLabel}>
                {translate(TREE_SECTION_META[section].labelKey)}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Typography sx={classes.modeHint}>{translate(meta.hintKey)}</Typography>

      {aiTreeSection === 'general' ? null : aiTreeSection === 'scripts' ? (
        <Box sx={classes.followRow}>
          <Box sx={classes.followText}>
            <Typography sx={classes.followTitle}>
              {translate('editorMode.aiFollowTrigger')}
            </Typography>
            <Typography sx={classes.followSub}>
              {translate('editorMode.aiFollowTriggerHint')}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={followLiveTrigger}
            onChange={(event) => setFollowLiveTrigger(event.target.checked)}
            sx={classes.followSwitch}
            inputProps={
              {
                'aria-label': translate(
                  'editorMode.aiFollowTrigger',
                  'Follow live trigger',
                ),
                'data-testid': 'ai-tree-follow-live-toggle',
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
        </Box>
      ) : (
        <Box sx={classes.filterChipsRow}>
          {FILTERS.map((definition) => {
            const isActive = filter === definition.id;
            const count = counts[definition.id];
            return (
              <Box
                key={definition.id}
                component="button"
                type="button"
                sx={[classes.filterChip, isActive && classes.filterChipActive]}
                onClick={() => setFilter(definition.id)}
                aria-pressed={isActive}
                data-testid={`ai-tree-filter-${definition.id}`}
              >
                {translate(definition.labelKey)}
                <Box
                  component="span"
                  sx={[
                    classes.filterChipCount,
                    definition.id === 'issues' &&
                      count > 0 &&
                      classes.filterChipCountIssues,
                  ]}
                >
                  {count}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={classes.scrollArea}>
        {aiTreeSection === 'general' ? (
          <GeneralTab />
        ) : aiTreeSection === 'scripts' ? (
          configuredEntities.length === 0 &&
          noScriptOptions.length === 0 &&
          newTriggerRows.length === 0 &&
          dynamicTriggerOptions.length === 0 &&
          spawnedTriggerOptions.length === 0 ? (
            <Typography sx={classes.emptyState}>
              {translate('editorMode.aiNoEntities')}
            </Typography>
          ) : (
            <Box sx={classes.sectionList}>
              <Box>
                {renderSectionHeader(
                  'editorMode.aiSpotConfigured',
                  configuredEntities.length,
                  sectionsCollapsed.configured,
                  () =>
                    setSectionsCollapsed((prev) => ({
                      ...prev,
                      configured: !prev.configured,
                    })),
                  'ai-tree-section-header-configured',
                )}
                {!sectionsCollapsed.configured ? (
                  configuredEntities.length > 0 ? (
                    <Box sx={classes.rowList}>
                      {configuredEntities.map(renderEntityRow)}
                    </Box>
                  ) : (
                    <Typography sx={classes.sectionEmptyHint}>
                      {translate('editorMode.aiNoneConfigured')}
                    </Typography>
                  )
                ) : null}
              </Box>
              {newTriggerRows.length > 0 ? (
                <Box>
                  {renderSectionHeader(
                    'editorMode.driftNewScriptsGroup',
                    newTriggerRows.length,
                    sectionsCollapsed.newInBuild,
                    () =>
                      setSectionsCollapsed((prev) => ({
                        ...prev,
                        newInBuild: !prev.newInBuild,
                      })),
                    'ai-tree-section-header-new',
                    classes.sectionHeaderAmber,
                  )}
                  {!sectionsCollapsed.newInBuild ? (
                    <Box sx={classes.rowList}>
                      {newTriggerRows.map(renderNewTriggerRow)}
                    </Box>
                  ) : null}
                </Box>
              ) : null}
              {dynamicTriggerOptions.length > 0 ? (
                <Box>
                  {renderSectionHeader(
                    'editorMode.driftDynamicTriggerGroup',
                    dynamicTriggerOptions.length,
                    sectionsCollapsed.dynamicTrigger,
                    () =>
                      setSectionsCollapsed((prev) => ({
                        ...prev,
                        dynamicTrigger: !prev.dynamicTrigger,
                      })),
                    'ai-tree-section-header-dynamic',
                    classes.sectionHeaderAmber,
                  )}
                  {!sectionsCollapsed.dynamicTrigger ? (
                    <Box sx={classes.rowList}>
                      {dynamicTriggerOptions.map(renderDynamicTriggerRow)}
                    </Box>
                  ) : null}
                </Box>
              ) : null}
              {spawnedTriggerOptions.length > 0 ? (
                <Box>
                  {renderSectionHeader(
                    'editorMode.driftSpawnedTriggerGroup',
                    spawnedTriggerOptions.length,
                    sectionsCollapsed.spawnedTrigger,
                    () =>
                      setSectionsCollapsed((prev) => ({
                        ...prev,
                        spawnedTrigger: !prev.spawnedTrigger,
                      })),
                    'ai-tree-section-header-spawned',
                    classes.sectionHeaderAmber,
                  )}
                  {!sectionsCollapsed.spawnedTrigger ? (
                    <Box sx={classes.rowList}>
                      {spawnedTriggerOptions.map(renderSpawnedTriggerRow)}
                    </Box>
                  ) : null}
                </Box>
              ) : null}
              <Box>
                {renderSectionHeader(
                  'editorMode.driftNoScriptGroup',
                  noScriptOptions.length,
                  sectionsCollapsed.empty,
                  () =>
                    setSectionsCollapsed((prev) => ({
                      ...prev,
                      empty: !prev.empty,
                    })),
                  'ai-tree-section-header-empty',
                )}
                {!sectionsCollapsed.empty ? (
                  noScriptOptions.length > 0 ? (
                    <Box sx={classes.rowList}>
                      {noScriptOptions.map(renderEmptyOptionRow)}
                    </Box>
                  ) : (
                    <Typography sx={classes.sectionEmptyHint}>
                      {translate('editorMode.aiAllConfigured')}
                    </Typography>
                  )
                ) : null}
              </Box>
            </Box>
          )
        ) : visible.length === 0 ? (
          <Typography sx={classes.emptyState}>
            {filter === 'all'
              ? translate('editorMode.aiModeEmpty')
              : translate('editorMode.aiFilterEmpty')}
          </Typography>
        ) : (
          <Box sx={classes.rowList}>{visible.map(renderEntityRow)}</Box>
        )}
      </Box>

      {aiTreeSection === 'general' ? null : (
        <Box sx={classes.footer}>
          <Box
            component="button"
            ref={createButtonRef}
            type="button"
            sx={classes.createButton}
            onClick={handleCreateClick}
            aria-haspopup="dialog"
            aria-expanded={popoverOpen}
            aria-label={createLabel}
            data-testid="ai-tree-create-btn"
          >
            <AddIcon sx={{ fontSize: 16 }} />
            {createLabel}
          </Box>
        </Box>
      )}

      <CreateEntityPopover
        open={popoverOpen}
        anchorEl={createButtonRef.current}
        title={createLabel}
        placeholder={createPlaceholder}
        options={createOptions}
        liveOption={liveOption}
        onClose={() => setPopoverOpen(false)}
        onPick={handleStartCreate}
      />

      {scriptDialogState ? (
        <AssistantScriptDialogWrapper
          open
          assistantScript={null}
          trigger={scriptDialogState.trigger}
          title={scriptDialogState.trigger}
          mapName={mapName}
          brandingId={brandingId}
          defaultTriggerType={scriptDialogState.triggerType}
          language={audioLanguage}
          onSave={handleScriptSaved}
          onClose={() => setScriptDialogState(null)}
        />
      ) : null}

      {knowledgeDialogState ? (
        <AssistantContextDialogWrapper
          open
          tag={knowledgeDialogState.trigger}
          title={
            formatSpotRegion(knowledgeDialogState.trigger) ||
            knowledgeDialogState.trigger
          }
          mapName={mapName}
          onSave={handleKnowledgeSubmit}
          onClose={() => setKnowledgeDialogState(null)}
        />
      ) : null}
    </Box>
  );
};

export default AiScriptsTreeSection;
